import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGetTasksQuery, useGetProjectsQuery } from "../../services/rtkClient";
import Navbar from "../../components/Navbar";
import "./Calendar.css";
import {
    Folder,
    ClipboardList as Tasks,
    Calendar as CalendarIcon,
    Menu as Bars,
    ChevronLeft,
    ChevronRight,
    Plus,
    Users,
    LayoutGrid as ThLarge,
    ChevronDown,
    Download
} from "lucide-react";

const Calendar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);
    const [projectsOpen, setProjectsOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);

    const projectName = location.state?.projectName || sessionStorage.getItem('projectName') || "";
    const [currentProjectName, setCurrentProjectName] = useState(projectName || "New Workspace");
    const [filterCriteria, setFilterCriteria] = useState({ type: null, value: null });

    const getCurrentUserEmail = () => {
        try {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                return user.email || user.username || "default";
            }
        } catch (err) { }
        return "default";
    };

    const { data: serverTasksData } = useGetTasksQuery();
    const { data: serverProjectsData } = useGetProjectsQuery();

    const recentProjects = useMemo(() => {
        const raw = serverProjectsData?.data || serverProjectsData?.schema || (Array.isArray(serverProjectsData) ? serverProjectsData : []);
        return raw.map(p => ({
            id: p.id || p.projectId,
            name: p.name || p.projectName || "Unnamed Project"
        }));
    }, [serverProjectsData]);

    useEffect(() => {
        if (!location.state?.projectName && recentProjects.length > 0) {
            setCurrentProjectName(recentProjects[0].name);
        }
    }, [recentProjects, location.state?.projectName]);

    // Flatten tasks if they come in status buckets (like from TaskTools)
    const tasks = useMemo(() => {
        const rawTasks = serverTasksData?.data || serverTasksData?.schema || (Array.isArray(serverTasksData) ? serverTasksData : (location.state?.tasks || []));
        if (!rawTasks) return [];
        let all = [];
        if (Array.isArray(rawTasks)) {
            all = rawTasks;
        } else {
            all = Object.values(rawTasks).flat();
        }

        if (!filterCriteria.type || !filterCriteria.value) return all;

        return all.filter((task) => {
            if (filterCriteria.type === "Project") return true;
            if (filterCriteria.type === "Status") {
                const statusMap = {
                    "Completed": "Completed",
                    "In Progress": "In Progress",
                    "Hold": "On Hold"
                };
                return task.status === (statusMap[filterCriteria.value] || filterCriteria.value);
            }
            return true;
        });
    }, [location.state?.tasks, filterCriteria]);

    // State for dynamic calendar
    const [currentDate, setCurrentDate] = useState(new Date(2026, 1, 1)); // Start at Feb 2026 as per user snippet

    // Calendar logic helpers
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Generate days for the grid
    const calendarDays = useMemo(() => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const prevMonthLastDay = new Date(year, month, 0).getDate();

        const result = [];

        // Days from previous month to fill the first week
        for (let i = firstDayOfMonth - 1; i >= 0; i--) {
            result.push({
                day: prevMonthLastDay - i,
                type: "other-month",
                monthOffset: -1
            });
        }

        // Days for current month
        const today = new Date();
        for (let d = 1; d <= daysInMonth; d++) {
            const isToday = today.getDate() === d &&
                today.getMonth() === month &&
                today.getFullYear() === year;

            // Map tasks to this day
            // Simplified matching: look for day in task.date string or task.created
            const dayTasks = tasks.filter(t => {
                const tDate = new Date(t.date || t.created || t.updated);
                return !isNaN(tDate) &&
                    tDate.getDate() === d &&
                    tDate.getMonth() === month &&
                    tDate.getFullYear() === year;
            });

            result.push({
                day: d,
                today: isToday,
                tasks: dayTasks.map(t => ({
                    title: t.title || t.task,
                    assignee: t.assignee || "User",
                    category: (t.priority === "High" ? "design" : t.status === "In Progress" ? "development" : "review")
                }))
            });
        }

        // Days from next month to fill the grid (up to 42 cells for 6 weeks)
        const currentCount = result.length;
        for (let i = 1; i <= (42 - currentCount); i++) {
            result.push({
                day: i,
                type: "other-month",
                monthOffset: 1
            });
        }

        return result;
    }, [year, month, tasks]);

    const changeMonth = (offset) => {
        setCurrentDate(new Date(year, month + offset, 1));
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const handleSubItemClick = (type, value) => {
        if (type === "Project") {
            setCurrentProjectName(value);
            setFilterCriteria({ type: "Project", value });
        } else if (type === "Status") {
            if (value === "All") {
                setFilterCriteria({ type: null, value: null });
            } else {
                setFilterCriteria({ type: "Status", value });
            }
        }
    };

    const handleExport = () => {
        if (tasks.length === 0) {
            alert("No tasks to export");
            return;
        }

        const headers = ["Task", "Status", "Priority", "Assignee"];
        const csvRows = tasks.map(t => [
            `"${t.title || t.task || 'Untitled'}"`,
            t.status || 'Planned',
            t.priority || 'Medium',
            `"${t.assignee || 'Unassigned'}"`
        ].join(","));

        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `calendar_${currentProjectName.replace(/\s+/g, '_').toLowerCase()}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
        <div className="calendar-page-wrapper">
            <Navbar />
            <div className="calendar-page-container">
                {isSidebarVisible && (
                    <aside className="lp-sidebar">
                        <div className="lp-sidebar-nav">
                            <div
                                className="lp-nav-item"
                                onClick={() => navigate()}
                                style={{ cursor: "pointer", backgroundColor: "#0052CC", color: "#FFFFFF" }}
                            >
                                <span className="lp-nav-icon"  >
                                    <ThLarge size={18} />
                                </span>
                                Dashboard
                            </div>
                        </div>

                        <div className="lp-nav-section-header">
                            <span>New Project</span>
                            <span className="lp-section-add" onClick={() => navigate('/onboarding')} style={{ cursor: 'pointer' }}>
                                <Plus size={14} />
                            </span>
                        </div>

                        <div className="lp-sidebar-nav secondary">
                            <div
                                className="lp-nav-item"
                                onClick={() => setProjectsOpen(!projectsOpen)}
                                style={{ cursor: "pointer" }}
                            >
                                <span className="lp-nav-icon">
                                    <Folder size={18} />
                                </span>
                                Projects
                                <span className="lp-nav-arrow">
                                    {projectsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </span>
                            </div>
                            {projectsOpen && (
                                <div className="lp-sub-items">
                                    {recentProjects.map((proj, idx) => (
                                        <div key={idx} className="lp-sub-item" onClick={() => handleSubItemClick("Project", proj.name)}>{proj.name}</div>
                                    ))}
                                </div>
                            )}

                            <div
                                className="lp-nav-item"
                                onClick={() => setStatusOpen(!statusOpen)}
                                style={{ cursor: "pointer" }}
                            >
                                <span className="lp-nav-icon">
                                    <Tasks size={18} />
                                </span>
                                Status
                                <span className="lp-nav-arrow">
                                    {statusOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </span>
                            </div>
                            {statusOpen && (
                                <div className="lp-sub-items">
                                    <div
                                        className="lp-sub-item"
                                        onClick={() => handleSubItemClick("Status", "All")}
                                    >
                                        All
                                    </div>
                                    <div
                                        className="lp-sub-item"
                                        onClick={() => handleSubItemClick("Status", "Completed")}
                                    >
                                        Completed
                                    </div>
                                    <div
                                        className="lp-sub-item"
                                        onClick={() => handleSubItemClick("Status", "In Progress")}
                                    >
                                        In Progress
                                    </div>
                                    <div
                                        className="lp-sub-item"
                                        onClick={() => handleSubItemClick("Status", "Hold")}
                                    >
                                        Hold
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="lp-sidebar-footer">
                            <button className="lp-invite-btn">
                                <Users size={16} /> Invite People
                            </button>
                        </div>
                    </aside>
                )}

                <main className="calendar-main-content">
                    <header className="lp-header">
                        <div className="lp-header-top">
                            <button
                                className="sidebar-toggle-btn"
                                onClick={() => setIsSidebarVisible(!isSidebarVisible)}
                            >
                                <Bars size={18} />
                            </button>
                            <div className="lp-breadcrumb">
                                <Folder className="lp-breadcrumb-icon" size={14} /> {projectName}
                            </div>
                        </div>
                        <div className="lp-header-title">
                            <div className="lp-project-avatar" style={{ background: "linear-gradient(135deg, #0052cc, #6554c0)" }}>
                                <Tasks size={18} />
                            </div>
                            <h1>{currentProjectName}</h1>
                        </div>

                        <div className="lp-tabs">
                            <div className="lp-tab" onClick={() => navigate("/summary", { state: { tasks, projectName } })}>Summary</div>
                            <div className="lp-tab" onClick={() => navigate("/tasks", { state: { tasks, projectName } })}>Board</div>
                            <div className="lp-tab" onClick={() => navigate("/listpage", { state: { tasks, projectName } })}>Tasks List</div>
                            <div className="lp-tab" onClick={() => navigate("/timeline", { state: { tasks, projectName } })}>Timeline</div>
                            <div className="lp-tab active">Calendar</div>
                        </div>
                    </header>

                    <div className="calendar-content-scroll">
                        <div className="calendar-view-card">
                            <div className="calendar-view-header">
                                <button className="cal-nav-btn" onClick={() => changeMonth(-1)}><ChevronLeft size={18} /></button>
                                <h2>{monthNames[month]} {year}</h2>
                                <button className="cal-nav-btn" onClick={() => changeMonth(1)}><ChevronRight size={18} /></button>
                                <button className="btn-today" onClick={goToToday}>Today</button>
                                {filterCriteria.value && (
                                    <button
                                        className="lp-tool-btn"
                                        onClick={() => setFilterCriteria({ type: null, value: null })}
                                        style={{ color: '#0052CC', fontWeight: 'bold' }}
                                    >
                                        Clear filter: {filterCriteria.value}
                                    </button>
                                )}
                                <button className="lp-tool-btn" style={{ marginLeft: 'auto' }} onClick={handleExport}>
                                    <Download size={16} /> Export
                                </button>
                            </div>

                            <div className="calendar-grid-container">
                                <div className="calendar-weekdays">
                                    {weekDays.map(d => <div key={d} className="weekday-header">{d}</div>)}
                                </div>
                                <div className="calendar-days-grid">
                                    {calendarDays.map((d, i) => (
                                        <div key={i} className={`calendar-day-cell ${d.type || ""} ${d.today ? "today" : ""}`}>
                                            <span className="day-number">{d.day}</span>
                                            <div className="cell-tasks">
                                                {d.tasks?.map((t, ti) => (
                                                    <div key={ti} className={`cal-task-item ${t.category}`}>
                                                        {t.title} - {t.assignee}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="calendar-legend">
                                <div className="legend-item"><span className="dot design"></span> High Priority</div>
                                <div className="legend-item"><span className="dot development"></span> In Progress</div>
                                <div className="legend-item"><span className="dot review"></span> Completed/Review</div>
                                <div className="legend-item"><span className="dot testing"></span> QA/Testing</div>
                                <div className="legend-item"><span className="dot deployment"></span> Deployment</div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div >
    );
};

export default Calendar;
